import { Button, Modal } from "@mantine/core";
import type { ButtonCssVariables, ModalProps } from "@mantine/core";
import { modals } from "@mantine/modals";
import * as React from "react";
import type { ReturnInwardError } from "../types";

/** Bo 16, không thanh tiêu đề mặc định, nền mờ tối, đổ bóng (cùng kiểu hộp thoại màn xuất bán) */
const DIALOG_MODAL_PROPS: Pick<ModalProps, "radius" | "padding" | "withCloseButton" | "overlayProps" | "styles"> = {
  radius: 16,
  padding: 0,
  withCloseButton: false,
  overlayProps: { color: "#161A1E", backgroundOpacity: 0.48 },
  styles: {
    content: { boxShadow: "0 24px 64px rgba(0, 0, 0, 0.28)", color: "#1B1F24", fontSize: 14 },
  },
};

type ButtonVars = Partial<Record<ButtonCssVariables["root"], string>>;

const BUTTON_SIZE: ButtonVars = { "--button-height": "44px", "--button-radius": "10px", "--button-fz": "14px" };

const PRIMARY_BUTTON: ButtonVars = {
  ...BUTTON_SIZE,
  "--button-padding-x": "20px",
  "--button-bg": "#1F5FAE",
  "--button-hover": "#174A88",
  "--button-color": "#FFFFFF",
  "--button-hover-color": "#FFFFFF",
  "--button-bd": "1px solid transparent",
};

const SECONDARY_BUTTON: ButtonVars = {
  ...BUTTON_SIZE,
  "--button-padding-x": "18px",
  "--button-bg": "#FFFFFF",
  "--button-hover": "#F6F7F4",
  "--button-color": "#1B1F24",
  "--button-hover-color": "#1B1F24",
  "--button-bd": "1px solid #D5D9D2",
};

/** Nút dạng liên kết ("Tới dòng", "Tới dòng đơn", "Chọn kho") */
const LINK_BUTTON: ButtonVars = {
  "--button-height": "auto",
  "--button-padding-x": "0px",
  "--button-fz": "14px",
  "--button-bg": "transparent",
  "--button-hover": "transparent",
  "--button-color": "#1F5FAE",
  "--button-hover-color": "#174A88",
  "--button-bd": "none",
};

interface DialogButtonProps {
  tone?: "primary" | "secondary";
  autoFocus?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

/** Nút chân hộp thoại: cao 44, bo 10 */
const DialogButton = ({ tone = "primary", autoFocus, onClick, children }: DialogButtonProps) => (
  <Button
    variant="filled"
    fw={600}
    data-autofocus={autoFocus || undefined}
    onClick={onClick}
    vars={() => ({ root: tone === "primary" ? PRIMARY_BUTTON : SECONDARY_BUTTON })}
  >
    {children}
  </Button>
);

interface DialogHeaderProps {
  icon: React.ReactNode;
  iconBackground: string;
  title: React.ReactNode;
  description?: React.ReactNode;
}

/** Đầu hộp thoại: ô icon, tiêu đề 19px đậm (Modal.Title để hộp có aria-labelledby), mô tả màu phụ */
const DialogHeader = ({ icon, iconBackground, title, description }: DialogHeaderProps) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "24px 24px 16px" }}>
    <div
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 12,
        background: iconBackground,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </div>
    <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
      <Modal.Title style={{ margin: 0, fontSize: 19, fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" }}>
        {title}
      </Modal.Title>
      {description && <p style={{ margin: 0, color: "#5C6570", lineHeight: 1.5 }}>{description}</p>}
    </div>
  </div>
);

const DialogFooter = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "20px 24px 24px" }}>{children}</div>
);

const LockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8A4B05" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const PrinterIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1F5FAE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
    <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
  </svg>
);

const BLOCKING_MODAL_ID = "return-inward-blocking-errors";
const PRINT_MODAL_ID = "return-inward-print-options";

/** Nhãn nút đi tới chỗ cần xử lý; null nếu vướng mắc không gắn với chỗ nào */
const goToLabel = (error: ReturnInwardError) => {
  if (error.code === "NO_WAREHOUSE") return "Chọn kho";
  if (error.orderSerial) return "Tới dòng đơn";
  if (error.serial) return "Tới dòng";
  return null;
};

interface BlockingErrorsProps {
  title: string;
  errors: ReturnInwardError[];
  onGoTo?: (error: ReturnInwardError) => void;
  onClose: () => void;
}

const BlockingErrors = ({ title, errors, onGoTo, onClose }: BlockingErrorsProps) => (
  <div>
    <DialogHeader
      icon={<LockIcon />}
      iconBackground="#FFF1DC"
      title={title}
      description="Sửa các mục dưới đây rồi thử lại."
    />
    <ol
      style={{
        listStyle: "none",
        margin: 0,
        padding: "0 24px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxHeight: "50dvh",
        overflowY: "auto",
      }}
    >
      {errors.map((error, index) => {
        const label = onGoTo ? goToLabel(error) : null;
        return (
          <li
            key={`${error.code}-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 14,
              border: "1px solid #F1D9B5",
              background: "#FFF9EF",
              borderRadius: 12,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 999,
                background: "#FFFFFF",
                border: "1px solid #F1D9B5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                color: "#8A4B05",
              }}
            >
              {index + 1}
            </span>
            <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2, overflowWrap: "anywhere" }}>
              <span style={{ fontWeight: 600 }}>{error.message}</span>
              {error.detail && <span style={{ color: "#5C6570", fontSize: 13 }}>{error.detail}</span>}
            </div>
            {label && onGoTo && (
              <Button
                variant="subtle"
                fw={600}
                style={{ flexShrink: 0 }}
                vars={() => ({ root: LINK_BUTTON })}
                onClick={() => onGoTo(error)}
              >
                {label}
              </Button>
            )}
          </li>
        );
      })}
    </ol>
    <DialogFooter>
      <DialogButton autoFocus onClick={onClose}>
        Đã hiểu
      </DialogButton>
    </DialogFooter>
  </div>
);

/**
 * Liệt kê toàn bộ lỗi chặn lưu / hoàn thành trong một hộp thoại.
 * Có `onGoTo` thì vướng mắc gắn với dòng / kho có nút đi tới chỗ đó: đóng hộp rồi gọi `onGoTo`.
 */
export const showBlockingErrors = (
  title: string,
  errors: ReturnInwardError[],
  onGoTo?: (error: ReturnInwardError) => void,
) => {
  const close = () => modals.close(BLOCKING_MODAL_ID);
  modals.open({
    modalId: BLOCKING_MODAL_ID,
    ...DIALOG_MODAL_PROPS,
    size: 560,
    yOffset: "20dvh",
    children: (
      <BlockingErrors
        title={title}
        errors={errors}
        onClose={close}
        onGoTo={
          onGoTo
            ? (error) => {
                close();
                onGoTo(error);
              }
            : undefined
        }
      />
    ),
  });
};

const PrintOptions = ({ onConfirm, onCancel }: { onConfirm: (withServices: boolean) => void; onCancel: () => void }) => {
  const [withServices, setWithServices] = React.useState(false);
  const checkboxId = React.useId();
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onConfirm(withServices);
      }}
    >
      <DialogHeader icon={<PrinterIcon />} iconBackground="#E6EEF8" title="Chọn loại phiếu Nhập Kho Đổi Trả" />
      <label
        htmlFor={checkboxId}
        style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 24px", cursor: "pointer", fontSize: 14 }}
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={withServices}
          onChange={(event) => setWithServices(event.currentTarget.checked)}
          style={{ width: 18, height: 18, margin: 0, accentColor: "#1F5FAE" }}
        />
        In dịch vụ đi kèm
      </label>
      <DialogFooter>
        <DialogButton tone="secondary" onClick={onCancel}>
          Huỷ
        </DialogButton>
        <Button type="submit" variant="filled" fw={600} data-autofocus vars={() => ({ root: PRIMARY_BUTTON })}>
          In phiếu
        </Button>
      </DialogFooter>
    </form>
  );
};

/** Hỏi tuỳ chọn in (có in dịch vụ đi kèm không). Trả về null nếu người dùng huỷ */
export const askPrintOptions = (): Promise<{ withServices: boolean } | null> =>
  new Promise((resolve) => {
    let answered = false;
    const answer = (value: { withServices: boolean } | null) => {
      answered = true;
      modals.close(PRINT_MODAL_ID);
      resolve(value);
    };
    modals.open({
      modalId: PRINT_MODAL_ID,
      ...DIALOG_MODAL_PROPS,
      size: 440,
      yOffset: "20dvh",
      onClose: () => {
        if (!answered) resolve(null);
      },
      children: (
        <PrintOptions onConfirm={(withServices) => answer({ withServices })} onCancel={() => answer(null)} />
      ),
    });
  });
